import { useState, useEffect, forwardRef } from "react";
import { Bell, Settings, CheckCircle, XCircle, Clock, AlertCircle, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface JobNotification {
  id: string;
  jobId: string;
  jobType: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  message: string;
  timestamp: string;
  read: boolean;
}

export const HeaderNotifications = forwardRef<HTMLButtonElement>((props, ref) => {
  const { currentBrandId } = useBrandManager();
  const { isVisible } = usePageVisibility();
  const [notifications, setNotifications] = useState<JobNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Real-time subscription for job notifications (visibility-aware)
  useEffect(() => {
    if (!currentBrandId || !isVisible) return;

    // Load existing notifications from localStorage
    const stored = localStorage.getItem(`job-notifications-${currentBrandId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      setNotifications(parsed);
      setUnreadCount(parsed.filter((n: JobNotification) => !n.read).length);
    }

    // Set up real-time listener for job status changes
    const channel = supabase
      .channel('header-job-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'job_queue',
          filter: `brand_id=eq.${currentBrandId}`
        },
        (payload) => {
          const { new: newRecord, old: oldRecord } = payload;
          
          if (oldRecord && newRecord && oldRecord.status !== newRecord.status) {
            const notification: JobNotification = {
              id: `${newRecord.id}-${Date.now()}`,
              jobId: newRecord.id,
              jobType: newRecord.job_type || 'Job',
              status: newRecord.status,
              message: getStatusMessage(newRecord.job_type, newRecord.status, newRecord.error_message),
              timestamp: new Date().toISOString(),
              read: false
            };

            setNotifications(prev => {
              const updated = [notification, ...prev].slice(0, 20); // Keep last 20
              localStorage.setItem(`job-notifications-${currentBrandId}`, JSON.stringify(updated));
              return updated;
            });

            setUnreadCount(prev => prev + 1);

            // Show browser notification if permission granted
            if (Notification.permission === 'granted') {
              new Notification(`Job ${notification.status}`, {
                body: notification.message,
                icon: '/favicon.ico'
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentBrandId, isVisible]);

  const getStatusMessage = (jobType: string, status: string, errorMessage?: string) => {
    switch (status) {
      case 'running':
        return `${jobType} is now processing...`;
      case 'completed':
        return `${jobType} completed successfully`;
      case 'failed':
        return `${jobType} failed${errorMessage ? `: ${errorMessage}` : ''}`;
      case 'cancelled':
        return `${jobType} was cancelled`;
      default:
        return `${jobType} status changed to ${status}`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      if (currentBrandId) {
        localStorage.setItem(`job-notifications-${currentBrandId}`, JSON.stringify(updated));
      }
      return updated;
    });
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const dismissNotification = (notificationId: string) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      if (currentBrandId) {
        localStorage.setItem(`job-notifications-${currentBrandId}`, JSON.stringify(updated));
      }
      return updated;
    });
    
    // Update unread count
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    setUnreadCount(0);
    if (currentBrandId) {
      localStorage.setItem(`job-notifications-${currentBrandId}`, JSON.stringify(updated));
    }
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
    if (currentBrandId) {
      localStorage.removeItem(`job-notifications-${currentBrandId}`);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          size="icon"
          className="h-8 w-8 relative"
          {...props}
        >
          <Settings className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-80 p-0 max-h-[500px] overflow-hidden" align="end">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">Job Notifications</h4>
            <Link 
              to="/job-management" 
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
              onClick={() => setIsOpen(false)}
            >
              See all jobs
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          {notifications.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={markAllAsRead}
              >
                Mark all read
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={clearAll}
              >
                Clear all
              </Button>
            </div>
          )}
        </div>

        <ScrollArea className="h-96">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
              <p className="text-xs mt-1">Job updates will appear here</p>
            </div>
          ) : (
            <div className="p-2">
              {notifications.map((notification, index) => (
                <div key={notification.id}>
                  <div
                    className={`p-3 rounded-md transition-colors ${
                      notification.read 
                        ? 'hover:bg-muted/50' 
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {getStatusIcon(notification.status)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="outline" className="text-xs">
                            {notification.status}
                          </Badge>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">
                              {new Date(notification.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                              onClick={() => dismissNotification(notification.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <p 
                          className={`text-sm mt-1 cursor-pointer ${
                            notification.read ? 'text-muted-foreground' : 'text-foreground'
                          }`}
                          onClick={() => !notification.read && markAsRead(notification.id)}
                        >
                          {notification.message}
                        </p>
                        {!notification.read && (
                          <div className="mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-6"
                              onClick={() => markAsRead(notification.id)}
                            >
                              Mark as read
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {index < notifications.length - 1 && <Separator className="my-1" />}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
});

HeaderNotifications.displayName = "HeaderNotifications";